import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import Button from '../../../components/common/Button';
import Modal from '../../../components/elements/Modal';
import Timer from '../../../components/elements/Timer';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { IPauseFields } from '../../../lib/interfaces/IClassicDuel';
import { teamActions } from '../../../store/slices/team.slice';
import * as S from '../styles';

import { HiddenAudio } from './styles';

interface IProps {
  title: string;
  btnTitle: string;
  setPauseModal: (value: boolean) => void;
  isActivePauseModal: boolean;
  pauseSettings: IPauseFields;
  isCloseAfterEnd?: boolean;
}

const PauseContainer: FC<IProps> = ({
  title,
  setPauseModal,
  isActivePauseModal,
  pauseSettings,
  isCloseAfterEnd,
  btnTitle,
}) => {
  const dispatch = useAppDispatch();
  const [duration, setDuration] = useState(60);
  const [key, setKey] = useState(`${title}_${new Date().getTime()}`);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const totalSeconds = pauseSettings.pauseTime.min * 60 + pauseSettings.pauseTime.sec;

    if (duration === totalSeconds) {
      return;
    }

    setDuration(totalSeconds);
    setKey(`${title}_${new Date().getTime()}`);
  }, [pauseSettings.pauseTime.min, pauseSettings.pauseTime.sec, duration, title]);

  const onPauseContinue = useCallback(() => {
    setKey(`${title}_${new Date().getTime()}`);
    if (isActivePauseModal) {
      dispatch(teamActions.stopPause());
      if (title === 'pause') {
        setPauseModal(false);
      }
    } else {
      dispatch(teamActions.startPause({}));
    }
  }, [isActivePauseModal, dispatch, title]);

  const onCloseModal = useCallback(() => {
    setKey(`${title}_${new Date().getTime()}`);
    setPauseModal(false);
    dispatch(teamActions.stopPause());
  }, [dispatch, setPauseModal, title]);

  const onUpdatePauseRemainingTime = useCallback(
    (remainingTime: number) => {
      if (isCloseAfterEnd && remainingTime <= 0) {
        setPauseModal(false);
      }

      dispatch(teamActions.setPauseRemainingTime({ remainingTime, isAutoEnd: isCloseAfterEnd }));
    },
    [dispatch, isCloseAfterEnd, setPauseModal],
  );

  const onCompletedPause = useCallback(
    (totalElapsedTime: number) => {
      isCloseAfterEnd && setKey(`${title}_${new Date().getTime()}`);

      if (pauseSettings.isEnabledEndOfPauseSound && audioRef.current) {
        audioRef.current.play();
      }

      return {
        shouldRepeat: isCloseAfterEnd,
        newInitialRemainingTime: totalElapsedTime,
      };
    },
    [isCloseAfterEnd, title, pauseSettings.isEnabledEndOfPauseSound],
  );

  return (
    <>
      <Modal
        onClose={onCloseModal}
        isActive={isActivePauseModal}
        title={title}
      >
        <S.PauseContainer>
          <Timer
            key={key}
            $isBlinking={pauseSettings.isStartBlinking}
            variant="pause"
            isPlaying={isActivePauseModal}
            duration={duration}
            onUpdate={onUpdatePauseRemainingTime}
            onComplete={onCompletedPause}
          />
          <Button
            variant="primary"
            onClick={onPauseContinue}
            $size="large"
          >
            {btnTitle}
          </Button>
        </S.PauseContainer>
      </Modal>
      <HiddenAudio>
        <audio
          ref={audioRef}
          src="/assets/sounds/gong.mp3"
          preload="auto"
        />
      </HiddenAudio>
    </>
  );
};

export default PauseContainer;
